import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FiEdit2 } from 'react-icons/fi';
import { fetchBillEmissions, clearBillEmissions } from '../billParsingSlice';
import { setSelectedEntity, setSelectedEP, fetchEmissionProfileTreeByEntity } from '../../emissions/emissionSlice';
import { formatDate, parseUserInfo } from '../../../utilities/common';
import { formatNumber } from '../../reports/reportHelper';
import { LoadingState } from '../../../utilities/constants';
import DataTable from '../../../components/DataTable';
import Empty from '../../../components/Empty';

const EmissionDataModal = ({ bill, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));
  const { billEmissions, billEmissionsStatus } = useSelector((state) => state.billParsing);
  const { entity, emissions } = billEmissions;
  const [editingRowId, setEditingRowId] = useState(null);

  useEffect(() => {
    dispatch(fetchBillEmissions({ orgId, upldflId: bill.upldflId }));
    return () => {
      dispatch(clearBillEmissions());
    };
  }, [dispatch, orgId, bill.upldflId]);

  const handleEditRow = useCallback(
    async (row) => {
      setEditingRowId(row.eId);
      try {
        dispatch(setSelectedEntity(row.eDiv.divId));
        await dispatch(fetchEmissionProfileTreeByEntity({ orgId, entityId: row.eDiv.divId }));
        dispatch(setSelectedEP(row.eEprflsrc.eprflsrcId));
        history.push(`/emissions/data/${row.eId}/edit`, {
          returnTo: `/emissions/bill-parsing?viewBill=${bill.upldflId}`
        });
      } finally {
        setEditingRowId(null);
      }
    },
    [dispatch, history, orgId, bill.upldflId]
  );

  const tableColumns = useMemo(
    () => [
      {
        Header: t('Bill/Invoice Number'),
        accessor: 'eContributorId'
      },
      {
        Header: t('Provider'),
        accessor: 'eProvider'
      },
      {
        Header: t('Contribution'),
        accessor: 'eContribution'
      },
      {
        Header: t('Unit'),
        accessor: 'eContributionUnit'
      },
      {
        Header: t('Period Start Date'),
        accessor: 'eStartDate',
        Cell: ({ value }) => formatDate(value)
      },
      {
        Header: t('Period End Date'),
        accessor: 'eEndDate',
        Cell: ({ value }) => formatDate(value)
      },
      {
        Header: t('CO2-e (t)'),
        accessor: 'eCo2emission',
        Cell: ({ value }) => formatNumber(value, '-')
      },
      {
        Header: t('Last Updated'),
        accessor: 'eModifiedDate',
        Cell: ({ value }) => formatDate(value)
      },
      {
        Header: '',
        accessor: 'actions',
        disableSortBy: true,
        Cell: ({ row }) => (
          <button
            className="btn btn-link p-0 text-muted"
            title={t('Edit')}
            disabled={editingRowId !== null}
            onClick={() => handleEditRow(row.original)}
          >
            {editingRowId === row.original.eId ? (
              <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
            ) : (
              <FiEdit2 />
            )}
          </button>
        )
      }
    ],
    [t, handleEditRow, editingRowId]
  );

  return (
    <Modal size="xl" show onHide={onClose}>
      <Modal.Header closeButton>
        <div>
          <Modal.Title>{t('Emission Data')}</Modal.Title>
          <div className="text-muted" style={{ fontSize: '13px' }}>
            {bill.upldflName}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div
          className="d-flex align-items-center mb-3 p-2 px-3"
          style={{ background: '#f8f9fa', borderRadius: '6px' }}
        >
          <span className="text-muted mr-2">{t('Entity')}:</span>
          <strong>{entity?.divName || '-'}</strong>
        </div>
        {billEmissionsStatus === LoadingState.pending && (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        )}
        {billEmissionsStatus === LoadingState.succeeded && emissions.length === 0 && (
          <Empty title={t('No emissions recorded for this invoice yet.')} />
        )}
        {billEmissionsStatus === LoadingState.succeeded && emissions.length > 0 && (
          <DataTable columns={tableColumns} data={emissions} defaultSort="eModifiedDate" pageLimit={10} />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EmissionDataModal;
